'use client';

import { BackArrow, Chevron } from '@ahamove/icons';
import { Box } from '@ahamove/icons/order';
import type {
  IntercityPackageDetail,
  IntercityPackageForm,
} from '@ahm/api-wrappers-core';
import { parseSafeValue } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import PackageDetailsModal from '@/components/modals/package-details-modal';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export const DEFAULT_PACKAGE_DETAIL_ITEM = {
  weight: 0,
  length: 10,
  width: 10,
  height: 10,
  quantity: 1,
} as IntercityPackageDetail;

export default function BookingPackageDetails() {
  const tPackageDetails = useTranslations('booking.package_details');
  const paths = useLocationStore.use.paths();

  const {
    packageDetail,
    packageImages,
    billableWeightData,
    getTotalLengthWidthHeight,
    isLoadingStore,
    pathInfo,
  } = useBookingStore((state) => state);

  const nationWidePathInfo = useMemo(() => {
    return pathInfo?.[1];
  }, [pathInfo]);

  const [openedModal, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  const isMobile = useIsMobile();

  const initialPackageForm: IntercityPackageForm = useMemo(() => {
    return {
      package_detail:
        packageDetail.length > 0
          ? packageDetail
          : [DEFAULT_PACKAGE_DETAIL_ITEM],
      package_images: packageImages ?? undefined,
      item_description: nationWidePathInfo?.item_description,
      item_value: nationWidePathInfo?.item_value,
      item_name: nationWidePathInfo?.item_name,
      item_order_id: nationWidePathInfo?.item_order_id,
    };
  }, [nationWidePathInfo, packageDetail, packageImages]);

  const totalLengthWidthHeight = useMemo(
    () => getTotalLengthWidthHeight(packageDetail),
    [packageDetail, getTotalLengthWidthHeight]
  );

  return (
    <>
      <Button
        fullWidth
        h="auto"
        radius="0"
        pl={{ base: rem(8), md: rem(12) }}
        py={{ base: rem(8), md: rem(12) }}
        pr="md"
        disabled={isLoadingStore || paths.length < 2}
        onClick={openModal}
        variant="white"
        leftSection={<Box className="m-1 size-6 md:m-0 md:size-8" />}
        rightSection={<Chevron className="size-4 md:size-6" />}
        justify="space-between"
        styles={{
          label: {
            width: '100%',
          },
        }}
      >
        <Stack gap={rem(4)} ta="left">
          <Title order={isMobile ? 6 : 4} c="neutral.7">
            {tPackageDetails('title')}
          </Title>
          {billableWeightData?.total_billable_weight ? (
            <Text size="sm" fw={500} c="neutral.7">
              {tPackageDetails.rich('package_details_highlight', {
                weight: parseSafeValue(
                  billableWeightData.total_billable_weight
                ),
                length: parseSafeValue(totalLengthWidthHeight.length),
                width: parseSafeValue(totalLengthWidthHeight.width),
                height: parseSafeValue(totalLengthWidthHeight.height),
                divider: () => (
                  <Divider
                    orientation="vertical"
                    h={rem(14)}
                    mx={rem(4)}
                    display="inline"
                  />
                ),
              })}
              ...
            </Text>
          ) : (
            <Text size="sm" fw={700} c="secondary.5">
              {tPackageDetails('required')}{' '}
              <Text
                lh="xs"
                size="sm"
                fw={700}
                c="red.4"
                component="span"
                styles={{
                  root: {
                    verticalAlign: 'middle',
                  },
                }}
              >
                *
              </Text>
            </Text>
          )}
        </Stack>
      </Button>
      <Modal
        opened={openedModal}
        onClose={closeModal}
        title={
          <Group gap="xl" align="center" justify="space-between" w="100%">
            {isMobile ? (
              <ActionIcon
                size={rem(24)}
                onClick={closeModal}
                variant="transparent"
              >
                <BackArrow width={24} height={24} />
              </ActionIcon>
            ) : null}
            <Title
              order={2}
              size={isMobile ? 'h4' : 'h2'}
              flex={1}
              ta={isMobile ? 'center' : 'left'}
            >
              {tPackageDetails('title')}
            </Title>
            <Space w={rem(24)} />
          </Group>
        }
        withCloseButton={!isMobile}
        fullScreen
        closeOnEscape={false}
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          header: {
            backgroundColor: 'white',
            minHeight: rem(44),
            boxShadow: isMobile
              ? 'none'
              : '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
            padding: isMobile
              ? `${rem(10)} ${rem(16)}`
              : `${rem(16)} ${rem(24)}`,
            border: isMobile
              ? `1px solid var(--mantine-color-neutral-1)`
              : 'none',
          },
          title: {
            width: '100%',
          },
          close: {
            '--cb-icon-size': rem(32),
            height: rem(32),
            width: rem(32),
            borderRadius: rem(32),
          },
          content: {
            backgroundColor: 'var(--mantine-color-neutral-0)',
          },
        }}
      >
        <PackageDetailsModal
          innerProps={{
            initialData: initialPackageForm,
          }}
          onClose={closeModal}
        />
      </Modal>
    </>
  );
}
